import { db } from './config';
import { collection, writeBatch, doc, Timestamp, getDocs, limit, query } from 'firebase/firestore';
import { fakerPT_BR as faker } from '@faker-js/faker';
import { SocialPost, SocialProfile } from '../../types/social';

const FISH_SPECIES = [
  'Tucunaré Azul', 'Tucunaré Pinima', 'Tucunaré Açu', 'Robalo Flecha', 'Robalo Peva',
  'Dourado', 'Pintado', 'Surubim', 'Pirarucu', 'Tambacu', 'Tambaqui', 'Pacu',
  'Piraíba', 'Pirarara', 'Traíra', 'Black Bass'
];

const LOCATIONS = [
  'Rio Araguaia, GO', 'Rio Negro, AM', 'Pantanal Norte, MT', 'Serra da Mesa, GO',
  'Epitácio, SP', 'Rio Paraguai, MS', 'Rio Madeira, RO', 'Litoral Norte, SP',
  'Passo do Landa, MS', 'Rio Xingu, PA'
];

const TECHNIQUES = [
  'Fly Fishing', 'Baitcasting', 'Pesca com Iscas Vivas', 'Trolling',
  'Pesca de Fundo', 'Jigging', 'Superfície/Zaras', 'Sub-Superfície'
];

const EQUIPMENT = [
  'Vara de Carbono 17lb', 'Carretilha Perfil Baixo', 'Isca Articulada',
  'Linha Multifilamento 0.30mm', 'Líder de Fluorocarbono', 'Snap Reforçado'
];

export class SocialSeederService {
  private static getRandomItem<T>(list: T[]): T {
    return list[Math.floor(Math.random() * list.length)];
  }

  private static getRandomDate(daysBack: number): Date {
    const now = new Date();
    const start = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
    return new Date(start.getTime() + Math.random() * (now.getTime() - start.getTime()));
  }

  static async seedProfiles(count: number, onLog: (msg: string, progress?: number) => void) {
    onLog(`👤 Seeding ${count} profiles...`, 0);
    const batch = writeBatch(db);
    const profilesCol = collection(db, 'social_profiles');

    for (let i = 0; i < count; i++) {
      const firstName = faker.person.firstName();
      const lastName = faker.person.lastName();
      const handle = faker.internet.username({ firstName, lastName }).toLowerCase().replace(/[^a-z0-9]/g, '.');
      const isBusiness = Math.random() > 0.8;
      
      // Trust & Verification Logic
      const isShadow = Math.random() > 0.4; // 60% are shadow profiles for testing
      const verificationLevel = isShadow ? 'none' : (Math.random() > 0.6 ? 'blue' : (Math.random() > 0.5 ? 'gold' : 'silver'));

      const profile: SocialProfile = {
        id: handle,
        handle,
        type: isBusiness ? 'business' : 'user',
        displayName: `${firstName} ${lastName}`,
        avatarUrl: `https://i.pravatar.cc/150?u=${handle}`,
        bio: faker.lorem.sentence(),
        location: {
          city: faker.location.city(),
          state: faker.location.state({ abbreviated: true })
        },
        stats: {
          followers: faker.number.int({ min: 10, max: 5000 }),
          following: faker.number.int({ min: 10, max: 1000 }),
          posts: faker.number.int({ min: 0, max: 100 }),
          reputation: faker.number.int({ min: 0, max: 1000 })
        },
        verification: {
          isShadow,
          isVerified: !isShadow,
          level: verificationLevel as any,
          cadastur: (isBusiness && verificationLevel === 'blue') ? {
            registrationNumber: faker.string.numeric(10),
            docUrl: 'https://fake-cadastur.gov.br/certificate.pdf'
          } : null,
          verifiedAt: !isShadow ? new Date() : null
        },
        moderation: {
          role: verificationLevel === 'blue' ? 'expert' : 'user',
          specialties: [this.getRandomItem(FISH_SPECIES)],
          reputationScore: faker.number.int({ min: 0, max: 100 })
        },
        settings: {
          isPublic: true,
          allowTagging: true,
          showContactInfo: isBusiness
        },
        createdAt: this.getRandomDate(365),
        updatedAt: new Date()
      };

      const docRef = doc(profilesCol, handle);
      batch.set(docRef, profile);

      if ((i + 1) % 50 === 0 || i === count - 1) {
        onLog(`   ...prepared ${i + 1} profiles`, ((i + 1) / count) * 100);
      }
    }

    await batch.commit();
    onLog(`✅ Successfully seeded ${count} profiles.`, 100);
  }

  static async seedPosts(count: number, daysBack: number, onLog: (msg: string, progress?: number) => void) {
    onLog(`📝 Seeding ${count} posts over ${daysBack} days...`, 0);
    
    // Get some existing handles to act as authors
    // Get some existing profiles to act as authors
    const profilesQuery = query(collection(db, 'social_profiles'), limit(100));
    const profilesSnapshot = await getDocs(profilesQuery);
    const profiles = profilesSnapshot.docs.map(d => ({
      id: d.id,
      handle: d.data().handle || d.id,
      displayName: d.data().displayName || d.id
    }));
    
    if (profiles.length === 0) {
      onLog("⚠️ No profiles found. Seed profiles first!");
      return;
    }

    const postsCol = collection(db, 'social_posts');
    let currentBatch = writeBatch(db);
    let batchCount = 0;

    for (let i = 0; i < count; i++) {
      const profile = this.getRandomItem(profiles);
      const handle = profile.handle;
      const authorName = profile.displayName;
      const category = this.getRandomItem(['catch', 'technique', 'location', 'equipment', 'story'] as const);
      const species = this.getRandomItem(FISH_SPECIES);
      const location = this.getRandomItem(LOCATIONS);
      const technique = this.getRandomItem(TECHNIQUES);
      const equip = this.getRandomItem(EQUIPMENT);
      const date = this.getRandomDate(daysBack);

      const post: SocialPost = {
        id: `post_${faker.string.uuid()}`,
        authorId: profile.id,
        authorHandle: handle,
        authorName: authorName,
        authorType: Math.random() > 0.8 ? 'business' : 'user',
        content: this.generateContent(category, species, location, technique, equip),
        mediaUrls: [
          `https://images.unsplash.com/photo-${faker.number.int({ min: 1500000000000, max: 1600000000000 })}?w=800`
        ],
        tags: [
          category,
          species.toLowerCase().replace(/\s+/g, ''),
          location.split(',')[0].toLowerCase().replace(/\s+/g, ''),
          'nossapesca',
          'pescaesportiva'
        ],
        category,
        significance: faker.number.int({ min: 1, max: 10 }),
        location: location,
        likesCount: faker.number.int({ min: 0, max: 500 }),
        commentsCount: faker.number.int({ min: 0, max: 50 }),
        metadata: {
          species: [species],
          equipment: [equip],
          location: {
            lat: -14 + (Math.random() * 10),
            lng: -50 + (Math.random() * 10)
          }
        },
        createdAt: date
      };

      const docRef = doc(postsCol, post.id);
      currentBatch.set(docRef, post);
      batchCount++;

      // Firestore batches are limited to 500 operations
      if (batchCount >= 450) {
        await currentBatch.commit();
        onLog(`   ...committed ${i + 1} posts`, ((i + 1) / count) * 100);
        currentBatch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await currentBatch.commit();
    }
    
    onLog(`✅ Successfully seeded ${count} posts.`, 100);
  }

  private static generateContent(cat: string, species: string, loc: string, tech: string, equip: string): string {
    const templates: Record<string, string[]> = {
      catch: [
        `Incrível registro de um ${species} capturado hoje em ${loc}. Muita adrenalina!`,
        `O monstro apareceu! Esse ${species} deu trabalho, mas a técnica de ${tech} não falha.`,
        `Pescaria inesquecível em ${loc}. Batida forte de um ${species} na superfície.`
      ],
      technique: [
        `Dica de ${tech}: Para buscar o ${species} em águas mais profundas, usei ${equip}.`,
        `Dominando a arte do ${tech}. O segredo está no trabalho da isca e paciência.`,
        `Treinando ${tech} hoje. Resultados consistentes com ${species} na região de ${loc}.`
      ],
      location: [
        `Explorando novos pontos em ${loc}. Estrutura perfeita para o ${species}.`,
        `Pôr do sol em ${loc}. O lugar mais bonito que já pesquei.`,
        `Dica de local: ${loc} está com o nível de água perfeito para ${tech}.`
      ],
      equipment: [
        `Review do dia: ${equip}. Resistência nota 10 na luta contra o ${species}.`,
        `Setup montado para o Araguaia: ${equip} montado com linha de alta performance.`,
        `Gosto muito de usar ${equip} quando o alvo é o ${species}.`
      ],
      story: [
        `Um dia que começou difícil mas terminou com recorde pessoal em ${loc}.`,
        `Pescando e preservando. O relato de hoje é sobre a importância do Catch & Release.`,
        `Mais do que peixe, a pesca é sobre as amizades e a conexão com a natureza em ${loc}.`
      ]
    };

    return this.getRandomItem(templates[cat] || templates.story);
  }
}
